import { call, put, takeLatest, select } from 'redux-saga/effects';
import backupService from '../../../services/backupService';
import {
  fetchInitRequest,
  fetchInitSuccess,
  fetchInitFailure,
  fetchBackupConfigRequest,
  fetchBackupConfigSuccess,
  fetchBackupConfigFailure,
  saveBackupConfigRequest,
  saveBackupConfigSuccess,
  saveBackupConfigFailure,
  manualBackupRequest,
  manualBackupSuccess,
  manualBackupFailure,
  fetchBackupLogsRequest,
  fetchBackupLogsSuccess,
  fetchBackupLogsFailure,
  selectBackupLogsPage,
  selectBackupLogsSize,
  selectFilterUnitIds,
  selectFilterTypes,
  selectFilterStatuses,
  selectFilterIsMarks,
  selectFilterFromDate,
  selectFilterToDate,
  markBackupLogRequest,
  markBackupLogSuccess,
  markBackupLogFailure,
  restoreBackupLogRequest,
  restoreBackupLogSuccess,
  restoreBackupLogFailure,
  // Restore logs (Tab 3)
  fetchRestoreLogsRequest,
  fetchRestoreLogsSuccess,
  fetchRestoreLogsFailure,
  selectRestoreLogsPage,
  selectRestoreLogsSize,
  selectFilterRestoreUnitIds,
  selectFilterRestoreTypes,
  selectFilterRestoreStatuses,
  selectFilterRestoreFromDate,
  selectFilterRestoreToDate,
} from './slice';

function* handleFetchInit() {
  try {
    const response = yield call(backupService.getInit);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    yield put(fetchInitFailure(error.message || 'Failed to fetch init data'));
  }
}

function* handleFetchBackupConfig(action) {
  try {
    const response = yield call(backupService.getBackupConfig, action.payload);
    yield put(fetchBackupConfigSuccess(response));
  } catch (error) {
    // If not found or error, set config to null (will clear the form)
    yield put(fetchBackupConfigFailure(error.message || 'Failed to fetch backup config'));
  }
}

function* handleSaveBackupConfig(action) {
  try {
    const { unitId, configData } = action.payload;
    const response = yield call(backupService.saveBackupConfig, unitId, configData);
    yield put(saveBackupConfigSuccess(response));
  } catch (error) {
    yield put(saveBackupConfigFailure(error.message || 'Failed to save backup config'));
  }
}

function* handleManualBackup(action) {
  try {
    const response = yield call(backupService.manualBackup, action.payload);
    yield put(manualBackupSuccess(response));
  } catch (error) {
    yield put(manualBackupFailure(error.message || 'Failed to trigger manual backup'));
  }
}

function* handleFetchBackupLogs(action) {
  try {
    const page = action.payload?.page || (yield select(selectBackupLogsPage));
    const size = action.payload?.size || (yield select(selectBackupLogsSize));
    const unitIds = yield select(selectFilterUnitIds);
    const types = yield select(selectFilterTypes);
    const statuses = yield select(selectFilterStatuses);
    const isMarks = yield select(selectFilterIsMarks);
    const fromDate = yield select(selectFilterFromDate);
    const toDate = yield select(selectFilterToDate);

    const params = { page, size };

    // Convert filter values to proper types for API
    // unitIds: List<int>
    if (unitIds.length > 0) {
      params.unitIds = unitIds.map((id) => parseInt(id, 10));
    }
    // types: List<int>
    if (types.length > 0) {
      params.types = types.map((type) => parseInt(type, 10));
    }
    // statuses: List<int>
    if (statuses.length > 0) {
      params.statuses = statuses.map((status) => parseInt(status, 10));
    }
    // isMarks: List<bool>
    if (isMarks.length > 0) {
      params.isMarks = isMarks.map((mark) => {
        if (typeof mark === 'boolean') return mark;
        if (mark === 'true' || mark === 1 || mark === '1') return true;
        if (mark === 'false' || mark === 0 || mark === '0') return false;
        return Boolean(mark);
      });
    }
    // fromDate: DateTime? - convert to ISO datetime (start of day)
    if (fromDate) {
      params.fromDate = `${fromDate}T00:00:00`;
    }
    // toDate: DateTime? - convert to ISO datetime (end of day)
    if (toDate) {
      params.toDate = `${toDate}T23:59:59`;
    }

    const response = yield call(backupService.getBackupLogs, params);
    yield put(fetchBackupLogsSuccess(response));
  } catch (error) {
    yield put(fetchBackupLogsFailure(error.message || 'Failed to fetch backup logs'));
  }
}

function* handleMarkBackupLog(action) {
  try {
    const response = yield call(backupService.markBackupLog, action.payload);
    yield put(markBackupLogSuccess(response));
    // Refresh backup logs after marking
    const page = yield select(selectBackupLogsPage);
    const size = yield select(selectBackupLogsSize);
    yield put(fetchBackupLogsRequest({ page, size }));
  } catch (error) {
    yield put(markBackupLogFailure(error.message || 'Failed to mark backup log'));
  }
}

function* handleRestoreBackupLog(action) {
  try {
    const response = yield call(backupService.restoreBackupLog, action.payload);
    yield put(restoreBackupLogSuccess(response));
    // Refresh backup logs after restore
    const page = yield select(selectBackupLogsPage);
    const size = yield select(selectBackupLogsSize);
    yield put(fetchBackupLogsRequest({ page, size }));
  } catch (error) {
    yield put(restoreBackupLogFailure(error.message || 'Failed to restore backup'));
  }
}

function* handleFetchRestoreLogs(action) {
  try {
    const page = action.payload?.page || (yield select(selectRestoreLogsPage));
    const size = action.payload?.size || (yield select(selectRestoreLogsSize));
    const unitIds = yield select(selectFilterRestoreUnitIds);
    const types = yield select(selectFilterRestoreTypes);
    const statuses = yield select(selectFilterRestoreStatuses);
    const fromDate = yield select(selectFilterRestoreFromDate);
    const toDate = yield select(selectFilterRestoreToDate);

    const params = { page, size };

    // Convert filter values to proper types for API
    // unitIds: List<int>
    if (unitIds.length > 0) {
      params.unitIds = unitIds.map((id) => parseInt(id, 10));
    }
    // types: List<int>
    if (types.length > 0) {
      params.types = types.map((type) => parseInt(type, 10));
    }
    // statuses: List<int>
    if (statuses.length > 0) {
      params.statuses = statuses.map((status) => parseInt(status, 10));
    }
    // fromDate: DateTime? - convert to ISO datetime (start of day)
    if (fromDate) {
      params.fromDate = `${fromDate}T00:00:00`;
    }
    // toDate: DateTime? - convert to ISO datetime (end of day)
    if (toDate) {
      params.toDate = `${toDate}T23:59:59`;
    }

    const response = yield call(backupService.getRestoreLogs, params);
    yield put(fetchRestoreLogsSuccess(response));
  } catch (error) {
    yield put(fetchRestoreLogsFailure(error.message || 'Failed to fetch restore logs'));
  }
}

export function* watchSyncDataActions() {
  yield takeLatest(fetchInitRequest.type, handleFetchInit);
  yield takeLatest(fetchBackupConfigRequest.type, handleFetchBackupConfig);
  yield takeLatest(saveBackupConfigRequest.type, handleSaveBackupConfig);
  yield takeLatest(manualBackupRequest.type, handleManualBackup);
  yield takeLatest(fetchBackupLogsRequest.type, handleFetchBackupLogs);
  yield takeLatest(markBackupLogRequest.type, handleMarkBackupLog);
  yield takeLatest(restoreBackupLogRequest.type, handleRestoreBackupLog);
  yield takeLatest(fetchRestoreLogsRequest.type, handleFetchRestoreLogs);
}
